import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ArrowUp, ArrowDown, Activity, CircleCheck, CircleX, Stethoscope, CalendarClock } from "lucide-react";
import { fmtTime } from "@emr/shared/india";
import { useAuth } from "@/context/AuthContext";
import { Has } from "@/components/Has";
import { AppointmentStatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api";
import { toast } from "sonner";
import type { AppointmentRow as ApptRow } from "@/lib/queries";
import { useState } from "react";
import { ReceptionVitalsDialog } from "./ReceptionVitalsDialog";
import { RebookDialog } from "./RebookDialog";
import { CheckInDialog } from "./CheckInDialog";

interface Props {
  appt: ApptRow;
  /** Whole-day calling serial (assigned at check-in). Rendered as a badge on
   * CheckedIn rows. Passed separately so list callers control visibility. */
  daySerial?: number | null;
  onReorder: (id: string, direction: "up" | "down") => void;
}

export function AppointmentRow({ appt, daySerial, onReorder }: Props) {
  const navigate = useNavigate();
  const { isAdmin, permissions } = useAuth();
  const qc = useQueryClient();
  const [vitalsOpen, setVitalsOpen] = useState(false);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [rebookOpen, setRebookOpen] = useState(false);
  const canEdit = isAdmin || permissions.has("appointments.edit");
  const canManage = isAdmin || permissions.has("appointments.manage");
  const canVitals = isAdmin || permissions.has("vitals.create");
  const canViewEmr = isAdmin || permissions.has("emr.view");
  const canCheckIn = canEdit && (isAdmin || permissions.has("emr.create"));

  // Lazy initializer: DOB → age is computed once per mount; the clock read
  // stays out of render (purity). Age re-syncs only on remount/navigation.
  const [age] = useState(() =>
    appt.dob ? Math.floor((Date.now() - new Date(appt.dob).getTime()) / (365.25 * 24 * 3600 * 1000)) : null,
  );

  // For checked-in patients, show the actual check-in time; otherwise show the slot arrival time.
  const displayTime = appt.checkInAt ?? appt.arrivalTime;

  const setStatus = async (status: "Completed" | "Cancelled") => {
    try {
      await apiFetch(`/appointments/${appt.id}`, { method: "PATCH", body: JSON.stringify({ status }) });
      qc.invalidateQueries({ queryKey: ["appointments"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(`Marked ${status}`);
    } catch (e) {
      const err = e as Error & { body?: { error?: string } };
      toast.error(err?.body?.error || "Failed");
    }
  };

  // ADMIN cancel of a CHECKED-IN appointment (2026-10-31): the API guard
  // (`cannot_cancel_after_checkin`) now lets admins through. Confirmed
  // first — the visit record stays; only the appointment is soft-cancelled.
  const cancelCheckedIn = async () => {
    if (!window.confirm(`Cancel ${appt.patientName ?? "this patient"}'s checked-in appointment? The visit record is kept.`)) return;
    await setStatus("Cancelled");
  };

  return (
    <div className={cn("flex items-center gap-2 border-b border-cream-100 px-3 py-2 hover:bg-cream-50/50 dark:border-dark-700 dark:hover:bg-dark-700/40", appt.status === "Completed" && "opacity-60")}>
      {/* Reorder arrows (Phase 3 split): Booked/Pending rows keep the
          within-slot queueOrder swap (appointments.manage); CheckedIn rows
          reorder by day serial via the admin-only day-reorder endpoint —
          arrows render for ADMINS only there (the API 403s non-admins). */}
      {(canManage && appt.status !== "CheckedIn") || (isAdmin && appt.status === "CheckedIn") ? (
        <div className="flex flex-col">
          <Button size="icon" variant="ghost" className="h-5 w-5 p-0" onClick={() => onReorder(appt.id, "up")}><ArrowUp size={12} /></Button>
          <Button size="icon" variant="ghost" className="h-5 w-5 p-0" onClick={() => onReorder(appt.id, "down")}><ArrowDown size={12} /></Button>
        </div>
      ) : null}
      {/* Day-serial badge (Phase 3): the whole-day calling order assigned at
          check-in. Shown on CheckedIn rows, left of the time column. */}
      {appt.status === "CheckedIn" && daySerial != null && (
        <span
          className="inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-maroon-600 px-1.5 text-xs font-bold text-cream-50"
          title={`Day serial ${daySerial} — calling order across all slots`}
        >
          {daySerial}
        </span>
      )}
      <div className="w-16 shrink-0 text-sm font-medium text-muted">{displayTime ? fmtTime(displayTime) : "—"}</div>
      <button className="min-w-0 flex-1 text-left" onClick={() => {
        if (appt.visitId && canViewEmr) navigate(`/emr/visits/${appt.visitId}`);
        else if (appt.patientId) navigate(`/patients/${appt.patientId}`);
      }}>
        <div className={cn("truncate text-sm text-ink dark:text-cream-50", appt.isNewPatient ? "font-bold" : "font-medium")} title={appt.isNewPatient ? "New patient (no previous visits)" : undefined}>{appt.patientName ?? "Unknown"}</div>
        <div className="truncate text-xs text-muted">{appt.clinicUhid}{age ? ` · ${age}y` : ""}{appt.gender ? ` · ${appt.gender[0]}` : ""}</div>
      </button>
      <div className="hidden flex-wrap gap-1 md:flex">
        {/* Flag badges — always honour the flag's colour (admin=Purple); never skip. */}
        {appt.flags?.map((f) => {
          const color = f.kind === "admin"
            ? "bg-purple-100 text-purple-800 border-purple-300"
            : (f.color || "bg-red-100 text-red-800 border-red-300");
          return (
            <Badge key={f.name} variant={null} className={cn("border px-1.5 py-0.5 text-[10px]", color)}>{f.name}</Badge>
          );
        })}
      </div>
      <AppointmentStatusBadge status={appt.status} />
      {canCheckIn && appt.status === "Booked" && (
        <Button size="sm" variant="outline" onClick={() => setCheckInOpen(true)}><Activity size={12} className="mr-1" /> Check-in</Button>
      )}
      {canVitals && appt.status === "CheckedIn" && (
        <Button size="sm" variant="outline" onClick={() => setVitalsOpen(true)}><Stethoscope size={12} className="mr-1" /> Vitals</Button>
      )}
      {isAdmin && appt.status === "CheckedIn" && (
        <Button size="sm" variant="ghost" onClick={() => { if (window.confirm(`Mark ${appt.patientName ?? "this patient"} completed?`)) setStatus("Completed"); }} title="Complete"><CircleCheck size={14} className="text-green-700" /></Button>
      )}
      {canEdit && (appt.status === "Booked" || appt.status === "Pending") && (
        <Button size="sm" variant="ghost" onClick={() => setRebookOpen(true)} title="Reschedule"><CalendarClock size={14} className="text-maroon-600" /></Button>
      )}
      {canEdit && appt.status === "Booked" && (
        <Button size="sm" variant="ghost" onClick={() => { if (window.confirm(`Cancel ${appt.patientName ?? "this patient"}'s appointment?`)) setStatus("Cancelled"); }} title="Cancel"><CircleX size={14} className="text-red-500" /></Button>
      )}
      {/* ADMIN cancel of a checked-in appointment (2026-10-31): sits on the
          same row as the patient's name (next to Complete). The API guard
          lets admins cancel checked-in rows; non-admins never see this. */}
      {isAdmin && appt.status === "CheckedIn" && (
        <Button size="sm" variant="ghost" onClick={cancelCheckedIn} title="Cancel checked-in appointment (admin)"><CircleX size={14} className="text-red-500" /></Button>
      )}
      <ReceptionVitalsDialog open={vitalsOpen} onOpenChange={setVitalsOpen} appointment={appt} />
      <CheckInDialog
        open={checkInOpen}
        onOpenChange={setCheckInOpen}
        target={checkInOpen ? {
          appointmentId: appt.id,
          patientId: appt.patientId ?? "",
          doctorId: appt.doctorId,
          patientName: appt.patientName,
          clinicUhid: appt.clinicUhid,
          photoR2Key: appt.photoR2Key,
          slot: appt.slot,
          arrivalTime: appt.arrivalTime,
          row: appt,
        } : null}
      />
      <RebookDialog open={rebookOpen} onOpenChange={setRebookOpen} appointment={appt} />
    </div>
  );
}
